import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Chip, FormControlLabel, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import SideDrawer from '../../components/SideDrawer'
import { wfApprove, wfReject, wfRequestInfo, wfWithdraw, wfCourierFreeze, serverMessage } from './api'
import {
  fmtKg, fmtDate, dimsText, ageLabel, isWfOpen, canAskForData, groupRounds,
  WF_ASK_ITEMS, WF_REJECT_REASONS, WF_MAX_SUBMISSIONS, WF_EVENT_TONE, WF_LEVEL_LABEL, WF_SOURCE_LABEL, coversText, packText} from './format'
import { WfStatusChip, LevelChip, ToneChip } from './shared/Chips'
import KeyValue, { Section } from './shared/KeyValue'

function EvidenceBlock({ evidence }) {
  const ev = evidence || {}
  const images = (ev.images || []).map((i) => i && i.signedLink).filter(Boolean)
  const video = ev.video && ev.video.signedLink
  const awbs = ev.dwsAwbs || []
  if (!images.length && !video && !awbs.length) {
    return <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Nothing attached. The merchant can add a packing video and up to five photographs while the request is open.</Typography>
  }
  return (
    <Stack spacing={1.5}>
      {video && (
        <Button size='small' variant='outlined' href={video} target='_blank' rel='noreferrer' startIcon={<PlayCircleOutlineIcon />} sx={{ fontSize: 11, alignSelf: 'flex-start' }}>
          Packing video
        </Button>
      )}
      {images.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {images.map((src, i) => (
            <Box key={`${i}-${src.slice(-24)}`} component='a' href={src} target='_blank' rel='noreferrer'
              sx={{ display: 'block', width: 96, height: 96, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb', bgcolor: '#f8fafc' }}>
              <Box component='img' src={src} alt={`evidence ${i + 1}`} loading='lazy' sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
          ))}
        </Box>
      )}
      {awbs.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#888', fontWeight: 600, mb: 0.5 }}>DWS records cited</Typography>
          <Stack direction='row' spacing={0.5} flexWrap='wrap' rowGap={0.5}>
            {awbs.map((a) => <Chip key={a} size='small' label={a} sx={{ height: 20, fontSize: 10.5, bgcolor: '#f3f4f6' }} />)}
          </Stack>
        </Box>
      )}
    </Stack>
  )
}

/** Rounds, oldest first; every event typed by kind — no prose is parsed to work out what happened. */
function Timeline({ history, round }) {
  const groups = groupRounds(history, round)
  return (
    <Stack spacing={1.5}>
      {groups.map((g) => (
        <Box key={g.round}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
            Round {g.round}{g.events.length === 0 ? ' — open, nothing recorded yet' : ''}
          </Typography>
          <Stack spacing={0.75} sx={{ pl: 1, borderLeft: '2px solid #e5e7eb' }}>
            {g.events.map((e, i) => (
              <Box key={`${e.kind}-${e.at}-${i}`} sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, alignItems: 'start' }}>
                <ToneChip tone={WF_EVENT_TONE[e.kind] || 'grey'} label={String(e.kind || '').replace(/_/g, ' ').toLowerCase()} />
                <Box>
                  <Typography sx={{ fontSize: 12, color: '#1a1a2e' }}>
                    {e.actor || 'System'} <Box component='span' sx={{ color: '#9ca3af' }}>· {fmtDate(e.at)}</Box>
                  </Typography>
                  {e.text && <Typography sx={{ fontSize: 12, color: '#4b5563' }}>{e.text}</Typography>}
                  {Array.isArray(e.requestedItems) && e.requestedItems.length > 0 && (
                    <Box component='ul' sx={{ m: 0, mt: 0.25, pl: 2, fontSize: 12, color: '#4b5563' }}>
                      {e.requestedItems.map((it) => <li key={it}>{it}</li>)}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  )
}

/**
 * Review one Merchant Weight Freeze request: the proposal, its evidence, the
 * round-by-round history and the three things Prozo can do — ask for more,
 * approve, reject. Approval records the agreed weight and supersedes any
 * earlier approval for the same subject; it changes NO bill until P6.
 * Rendered keyed on the request id by the shell below, so a new request is a
 * fresh mount with clean forms.
 */
/**
 * The courier freeze that sets this subject's bill today, beside the proposal. Approving a merchant
 * freeze below it lowers the bill once the merchant is LIVE, so the reviewer sees it before deciding.
 */
function CourierFreezeBlock({ requestId, proposedKg }) {
  const [state, setState] = useState({ loading: true, data: null, error: null })

  useEffect(() => {
    let cancelled = false
    setState({ loading: true, data: null, error: null })
    wfCourierFreeze(requestId)
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }) })
      .catch((err) => { if (!cancelled) setState({ loading: false, data: null, error: serverMessage(err, 'Could not read the courier freeze') }) })
    return () => { cancelled = true }
  }, [requestId])

  if (state.loading) return <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Checking the courier freeze…</Typography>
  if (state.error) return <Alert severity='warning' sx={{ fontSize: 12 }}>{state.error}</Alert>
  const d = state.data || {}
  const skus = d.skuFreezes || []
  const nothing = !d.wholeMerchantLock && skus.length === 0

  return (
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      {d.proposalBelowToday && (
        <Alert severity='warning' sx={{ fontSize: 12 }}>
          The proposal ({fmtKg(proposedKg)}) is below the courier freeze in force today ({fmtKg(d.todayFreezeKg)}).
          Once this merchant is LIVE, these bills can fall to {fmtKg(proposedKg)}, or to the typed weight if that is higher.
        </Alert>
      )}
      {nothing && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          No courier freeze applies to {d.skusChecked > 1 ? `these ${d.skusChecked} SKUs` : 'this SKU'} today.
        </Typography>
      )}
      {d.wholeMerchantLock && (
        <KeyValue
          columns={3}
          items={[
            { label: 'Whole-merchant lock', value: `${fmtKg(d.lockWeightKg)} per item`, strong: true },
            { label: 'Locked box', value: dimsText(d.lockDims) },
            { label: 'Effect today', value: 'Replaces the weight and box on every B2C order; per-product courier freezes do not apply' },
          ]}
        />
      )}
      {skus.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.75 }}>
            Per-product courier freezes: {skus.length} of {d.skusChecked} SKU{d.skusChecked === 1 ? '' : 's'} checked
            {d.wholeMerchantLock ? ' (overridden by the lock today)' : ''}
          </Typography>
          <Stack direction='row' spacing={0.5} flexWrap='wrap' rowGap={0.5}>
            {skus.slice(0, 30).map((f) => (
              <Chip key={f.sku} size='small' label={`${f.sku} · ${fmtKg(f.weightKg)}`} sx={{ height: 20, fontSize: 10.5, bgcolor: '#f3f4f6' }} />
            ))}
            {skus.length > 30 && <Chip size='small' label={`+${skus.length - 30} more`} sx={{ height: 20, fontSize: 10.5 }} />}
          </Stack>
        </Box>
      )}
      {d.note && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{d.note}</Typography>}
    </Box>
  )
}

export function FreezeRequestDrawerBody({ request: r, onChanged }) {
  const { enqueueSnackbar } = useSnackbar()
  const [mode, setMode] = useState(null)          // 'ask' | 'approve' | 'reject'
  const [items, setItems] = useState([])
  const [note, setNote] = useState('')
  const [remark, setRemark] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const openReq = isWfOpen(r.status)
  const proposed = r.status === 'PROPOSED'
  const basis = r.proposalBasis || null
  const askable = canAskForData(r)
  const askBlocked = !askable
    ? (r.status === 'ADDITIONAL_DATA_REQUESTED'
      ? 'Already asked — waiting on the merchant.'
      : `The merchant has used all ${WF_MAX_SUBMISSIONS} submissions; decide on the evidence on file.`)
    : ''

  const run = async (fn, okText) => {
    setBusy(true)
    try {
      const updated = await fn()
      enqueueSnackbar(okText, { variant: 'success' })
      setMode(null)
      if (onChanged) onChanged(updated)
    } catch (err) {
      enqueueSnackbar(serverMessage(err, 'Action failed'), { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const toggleItem = (it) => setItems((prev) => (prev.includes(it) ? prev.filter((x) => x !== it) : prev.concat(it)))
  const reasonNeedsRemark = reason === 'OTHER' && !remark.trim()

  return (
    <Box sx={{ width: '100%', display: 'grid', gap: 2 }}>
      <Stack direction='row' spacing={1} flexWrap='wrap' rowGap={1} alignItems='center'>
        <WfStatusChip status={r.status} />
        <LevelChip level={r.level} />
        <ToneChip tone='grey' label={`Submissions ${r.submissions || 0} of ${WF_MAX_SUBMISSIONS} · round ${r.round || 1}`} />
        {r.status === 'APPROVED' && (
          <ToneChip tone={r.billingActive ? 'success' : 'grey'} label={r.billingActive ? 'Billing on this freeze' : 'Agreement only — billing unchanged'} />
        )}
        {r.source && r.source !== 'MERCHANT' && <ToneChip tone='purple' label={WF_SOURCE_LABEL[r.source] || r.source} />}
      </Stack>

      {basis && (
        <Section title='Why Prozo proposed this weight'>
          <KeyValue
            columns={3}
            items={[
              { label: 'Accepted discrepancies counted', value: `${basis.acceptedCount} (rule: ${basis.minAccepts} in ${basis.windowMonths} months)`, strong: true },
              { label: 'Median accepted weight', value: basis.medianGm != null ? fmtKg(basis.medianGm / 1000) : null, strong: true },
              { label: 'Window', value: `${fmtDate(basis.windowFrom)} → ${fmtDate(basis.windowTo)}` },
              { label: 'Weights accepted (kg)', value: (basis.acceptedWeightsGm || []).map((g) => (g / 1000).toFixed(2)).join(', ') },
            ]}
          />
          {(basis.awbs || []).length > 0 && (
            <Stack direction='row' spacing={0.5} flexWrap='wrap' rowGap={0.5} sx={{ mt: 1.25 }}>
              {basis.awbs.map((a) => <Chip key={a} size='small' label={a} sx={{ height: 20, fontSize: 10.5, bgcolor: '#f3f4f6' }} />)}
            </Stack>
          )}
        </Section>
      )}

      <Section title='Proposal'>
        <KeyValue
          columns={3}
          items={[
            { label: 'Subject', value: r.subject, strong: true },
            { label: 'Chargeable weight proposed', value: fmtKg(r.proposedChargeKg), strong: true },
            { label: 'Covers', value: coversText(r) },
            { label: 'Dead weight', value: fmtKg(r.proposedDeadKg) },
            { label: 'Volumetric', value: fmtKg(r.proposedVolKg) },
            { label: 'Declared carton', value: dimsText(r.declaredDims) },
            { label: 'Packing material', value: packText(r) },
            { label: 'Declaration', value: r.declarationAccepted ? 'Accepted — declared packing material will be used' : 'Not accepted' },
            { label: 'Merchant', value: r.merchantName || r.merchantId },
            { label: 'Raised', value: `${fmtDate(r.raisedAt)}${r.raisedBy ? ` by ${r.raisedBy}` : ''}` },
            { label: 'Remarks', value: r.remarks },
          ]}
        />
        {(r.covers || []).length > 0 && (
          <Stack direction='row' spacing={0.5} flexWrap='wrap' rowGap={0.5} sx={{ mt: 1.25 }}>
            {r.covers.map((c) => <Chip key={c} size='small' label={c} sx={{ height: 20, fontSize: 10.5, bgcolor: '#f3f4f6' }} />)}
          </Stack>
        )}
      </Section>

      <Section title='Courier freeze in force today'>
        <CourierFreezeBlock requestId={r.id} proposedKg={r.proposedChargeKg} />
      </Section>

      <Section title='Evidence'>
        <EvidenceBlock evidence={r.evidence} />
      </Section>

      <Section title='History'>
        <Timeline history={r.history} round={r.round} />
      </Section>

      {!openReq && !proposed && (
        <Alert severity={r.status === 'APPROVED' ? 'success' : 'info'} sx={{ fontSize: 12 }}>
          {r.status === 'APPROVED' && `Approved by ${r.decidedBy || 'Prozo'} on ${fmtDate(r.decidedAt)} at ${fmtKg(r.proposedChargeKg)}. `}
          {r.status === 'REJECTED' && `Rejected by ${r.decidedBy || 'Prozo'} on ${fmtDate(r.decidedAt)} — ${(WF_REJECT_REASONS.find((x) => x.value === r.rejectReason) || {}).label || r.rejectReason || 'no reason recorded'}. `}
          {r.status === 'WITHDRAWN' && `Withdrawn by ${r.source === 'PROZO_PROPOSED' ? 'Prozo' : 'the merchant'} on ${fmtDate(r.decidedAt)}. `}
          {r.status === 'SUPERSEDED' && 'Superseded by a newer approval for the same subject. '}
          {r.status === 'PROPOSAL_DECLINED' && `The merchant declined Prozo's proposal on ${fmtDate(r.decidedAt)}. `}
          {r.decisionRemark ? `Remark: ${r.decisionRemark}` : ''}
          {r.status === 'APPROVED' && !r.billingActive ? " The merchant's bill is unchanged until billing on merchant freezes goes live for them." : ''}
          {r.status === 'APPROVED' && r.approvalNotice ? ` Shown to the merchant: “${r.approvalNotice}”` : ''}
        </Alert>
      )}

      {proposed && (
        <Section title='Proposal — waiting on the merchant'>
          <Alert severity='info' sx={{ fontSize: 12, mb: 1.5 }}>
            Prozo proposed this weight on {fmtDate(r.raisedAt)}. The merchant accepts or declines it in their panel; nothing bills
            until they accept AND the new weight rule is switched on for their account. Prozo can take the proposal off the table here.
          </Alert>
          <TextField fullWidth multiline minRows={2} size='small' label='Remark (optional)' value={remark} onChange={(e) => setRemark(e.target.value)} />
          <LoadingButton variant='outlined' color='warning' loading={busy} sx={{ mt: 1.5, fontSize: 12, fontWeight: 700 }}
            onClick={() => run(() => wfWithdraw(r.id, remark.trim()), `${r.requestRef}: proposal withdrawn`)}>
            Withdraw the proposal
          </LoadingButton>
        </Section>
      )}

      {openReq && (
        <Section title='Decision'>
          <Stack direction='row' spacing={1} flexWrap='wrap' rowGap={1}>
            <Tooltip title={askBlocked} disableHoverListener={!askBlocked}>
              <span>
                <Button size='small' variant={mode === 'ask' ? 'contained' : 'outlined'} disabled={!askable} onClick={() => setMode('ask')} sx={{ fontSize: 12 }}>
                  Ask for more data
                </Button>
              </span>
            </Tooltip>
            <Button size='small' variant={mode === 'approve' ? 'contained' : 'outlined'} color='success' onClick={() => setMode('approve')} sx={{ fontSize: 12 }}>
              Approve
            </Button>
            <Button size='small' variant={mode === 'reject' ? 'contained' : 'outlined'} color='error' onClick={() => setMode('reject')} sx={{ fontSize: 12 }}>
              Reject
            </Button>
          </Stack>

          {mode === 'ask' && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: '#4b5563', mb: 0.5 }}>
                What should the merchant send? This uses one of their {WF_MAX_SUBMISSIONS} submissions when they answer.
              </Typography>
              <Stack>
                {WF_ASK_ITEMS.map((it) => (
                  <FormControlLabel
                    key={it}
                    control={<Checkbox size='small' checked={items.includes(it)} onChange={() => toggleItem(it)} />}
                    label={<Typography sx={{ fontSize: 12 }}>{it}</Typography>}
                  />
                ))}
              </Stack>
              <TextField fullWidth multiline minRows={2} size='small' label='Note to the merchant (optional)' value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 1 }} />
              <LoadingButton
                variant='contained' loading={busy} disabled={!items.length && !note.trim()} sx={{ mt: 1.5, fontSize: 12, fontWeight: 700 }}
                onClick={() => run(() => wfRequestInfo(r.id, items, note.trim()), `${r.requestRef}: asked the merchant for more data`)}
              >
                Send the request for data
              </LoadingButton>
            </Box>
          )}

          {mode === 'approve' && (
            <Box sx={{ mt: 1.5 }}>
              <Alert severity='info' sx={{ fontSize: 12, mb: 1 }}>
                Approving records <b>{fmtKg(r.proposedChargeKg)}</b> as the agreed weight for <b>{r.subject}</b> and supersedes any earlier approval for it.
                It does <b>not</b> change the merchant's bill — billing on merchant freezes is switched on separately, per merchant.
                The merchant is told: “Approved. Billing on this weight starts when the new weight rule is switched on for your account.”
              </Alert>
              <TextField fullWidth multiline minRows={2} size='small' label='Remark (optional)' value={remark} onChange={(e) => setRemark(e.target.value)} />
              <LoadingButton variant='contained' color='success' loading={busy} sx={{ mt: 1.5, fontSize: 12, fontWeight: 700 }}
                onClick={() => run(() => wfApprove(r.id, remark.trim()), `${r.requestRef}: approved at ${fmtKg(r.proposedChargeKg)}`)}>
                Confirm approval
              </LoadingButton>
            </Box>
          )}

          {mode === 'reject' && (
            <Box sx={{ mt: 1.5 }}>
              <TextField select fullWidth size='small' label='Reason' value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mb: 1 }}>
                {WF_REJECT_REASONS.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12 }}>{o.label}</MenuItem>)}
              </TextField>
              <TextField fullWidth multiline minRows={2} size='small' label={reason === 'OTHER' ? 'Remark (required)' : 'Remark (optional)'} value={remark} onChange={(e) => setRemark(e.target.value)} error={reasonNeedsRemark} />
              <LoadingButton variant='contained' color='error' loading={busy} disabled={!reason || reasonNeedsRemark} sx={{ mt: 1.5, fontSize: 12, fontWeight: 700 }}
                onClick={() => run(() => wfReject(r.id, reason, remark.trim()), `${r.requestRef}: rejected`)}>
                Confirm rejection
              </LoadingButton>
            </Box>
          )}
        </Section>
      )}
    </Box>
  )
}

/** The drawer shell: header + a body keyed on the request, so each request opens on clean forms. */
export default function FreezeRequestDrawer({ request: r, open, onClose, onChanged }) {
  return (
    <SideDrawer
      open={!!(open && r)}
      setOpen={(v) => { if (!v) onClose() }}
      drawerWidth='56%'
      icon={<AcUnitOutlinedIcon />}
      title={r ? `${r.requestRef || 'Freeze request'} — ${r.subject || ''}` : ''}
      subtitle={r ? [r.merchantName, WF_LEVEL_LABEL[r.level] || r.level, r.raisedAt ? `raised ${ageLabel(r.raisedAt)} by ${r.raisedBy || 'merchant'}` : null].filter(Boolean).join(' · ') : ''}
    >
      {r && <FreezeRequestDrawerBody key={r.id} request={r} onChanged={onChanged} />}
    </SideDrawer>
  )
}
